import React, { useEffect, useId, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { keyframes } from '@mui/system';
import { BRAND_SANS_FONT } from '../../shared/design/theme';
import { MODEL_COPY, MULTI_MODEL_OPTIONS } from '../chat/ai-chat/constants';

// =============================================================================
// WorkflowComparison
// -----------------------------------------------------------------------------
// The proof diagram on the /vs pages: two panels that show WHERE the work
// happens and WHO picks the model, rather than arguing about output quality.
//
//   <WorkflowComparison competitor="cowork" />   on /vs/claude
//   <WorkflowComparison competitor="copilot" />  on /vs/copilot
//
// ONE PROP, TWO HONEST ARGUMENTS. The two competitors do not share a weakness,
// so the left panel is not one drawing with a swapped label:
//
//   cowork   THE ROUND TRIP. Claude Cowork is a separate surface. A copy of the
//            deck leaves the app, work happens over there, and a DIFFERENT file
//            comes back, so the job ends with two files to reconcile.
//
//   copilot  MODEL LOCK, AND NOTHING ELSE. Copilot is embedded in Office and
//            edits the live file, exactly like Restack. Drawing a round trip
//            here would be a lie, so the left panel draws the same live window
//            as the right one and the only difference is the pane: one model,
//            no picker. That parallel IS the argument.
//
// The right panel is the same in both variants, because Restack does not change
// when the competitor does: the deck stays open and is edited in place, and the
// model for each pass is the reader's pick.
//
// EVERY SLIDE MOCK IS 16:9. Slides are authored once in a SLIDE_W x SLIDE_H
// (160 x 90) unit space and placed with a single-argument uniform scale, so a
// mock cannot drift off ratio no matter where it lands. An audience that builds
// decks for a living reads a 1.52:1 rectangle as a mistake, which is what the
// previous version shipped.
//
// EVERYTHING DEPICTED IS SHIPPED BEHAVIOUR. In-place editing of the open file
// is what the Office bridges do; the model picker is the one in
// src/screens/chat/ai-chat/constants.js, which is where the three names drawn
// in the picker come from (see PICKER_MODEL_IDS). No maker names and no maker
// logos: the route a model takes to us is a backend detail, and stating it here
// would imply commercial relationships that do not exist.
//
// MOTION IS AN ENHANCEMENT AND NEVER THE ONLY READING. Every element is
// authored at its RESTING position, which is the complete, legible state: the
// travelling copy sits parked mid-corridor, the returned file is present in the
// app, the chart column is full height, one model row is selected. Keyframes
// move things away from that resting state and back. Drop the animation for any
// reason (reduced-motion preference, no matchMedia, pre-hydration prerendered
// HTML) and what is left is the finished diagram, not an empty frame. Only
// transform and opacity are animated, and the vocabulary is the site's own:
// translate-and-fade, pulse, grow, shimmer sweep.
//
// Design system: theme tokens for surfaces, text and hairlines so the graphic
// works in light and dark; the mode-aware warm accent for the Restack side;
// BRAND_SANS_FONT; MUI sx. No em dashes in copy.
// =============================================================================

// Fallback matches the warm accent the marketing surfaces already use, for the
// (test-only) case of a theme built without the accentBubble palette.
const ACCENT_FALLBACK = '#B83B1D';

// The diagram frame. Both panels share it so the two SVGs render at identical
// heights and the panels stay level in the grid.
const VIEW_W = 400;
const VIEW_H = 230;

// Slide authoring space. 160 / 90 is 16:9 exactly. Exported so the ratio itself
// is assertable rather than a claim in a comment.
export const SLIDE_W = 160;
export const SLIDE_H = 90;

// ---------------------------------------------------------------------------
// The model names drawn inside the picker affordance.
//
// ONE SOURCE, BY ID. These are ids into the shipped picker, never retyped
// names: MODEL_COPY for the Claude set, MULTI_MODEL_OPTIONS for everything
// routed elsewhere. Rename a model in constants.js and this graphic follows on
// its own, which is the only way a marketing page can promise the picker it
// draws is the picker the user gets.
//
// Three makers, all from the picker's Pro section, so the graphic shows a
// choice between peers rather than a trade down.
// ---------------------------------------------------------------------------
export const PICKER_MODEL_IDS = Object.freeze([
  'opus',
  'gpt-5-6-sol',
  'gemini-3-1-pro-preview',
]);

// Returns null for an id this build no longer offers, rather than throwing.
// The stale name is still caught loudly, but in the TEST run: see the assertion
// that every PICKER_MODEL_IDS entry resolves. Throwing here would throw at
// MODULE LOAD, because the label list below is built at import time and this
// module sits in the site's static import graph, so retiring a model in
// constants.js would take down every page rather than one diagram. A marketing
// graphic must never be able to do that.
export function pickerModelLabel(id) {
  if (MODEL_COPY[id]) {
    return MODEL_COPY[id].label;
  }
  const routed = MULTI_MODEL_OPTIONS.find((option) => option.id === id);
  return routed ? routed.label : null;
}

export const PICKER_MODEL_LABELS = Object.freeze(
  PICKER_MODEL_IDS.map(pickerModelLabel).filter(Boolean)
);

// ---------------------------------------------------------------------------
// Copy. Short: this is a diagram, not a paragraph.
//
// The right-hand half is deliberately near-identical across variants. Restack
// does not change when the competitor does, and a reader who lands on both
// pages should recognise the same product.
// ---------------------------------------------------------------------------
// Per variant. Both products edit the live file on the Copilot page, so opening
// that panel with "the deck stays open" would imply Copilot does not, which is
// the one claim this page must never make. There the difference is the picker
// alone, so the caption leads with it.
const RIGHT_CAPTION_COWORK =
  'The deck stays open and gets edited where it is. You pick the model for each pass, so the same slide can run again on a different one.';
const RIGHT_CAPTION_COPILOT =
  'You pick the model for each pass, so the same slide can run again on a different one without leaving the deck.';

export const DEFAULT_COMPETITOR = 'cowork';

export const COMPETITORS = Object.freeze({
  cowork: Object.freeze({
    id: 'cowork',
    // Which left-hand diagram to draw. 'roundTrip' is two windows and a file
    // that comes back changed; 'modelLock' is the live window with a locked
    // pane.
    diagram: 'roundTrip',
    left: Object.freeze({
      eyebrow: 'Claude Cowork',
      title: 'The round trip',
      outcome: 'Two files to reconcile',
      caption:
        'A copy goes out to a separate app. A different file comes back, and merging the two is your job.',
    }),
    right: Object.freeze({
      eyebrow: 'Restack',
      title: 'No round trip',
      outcome: 'One file, edited in place',
      caption: RIGHT_CAPTION_COWORK,
    }),
    alt: [
      'Two ways to work on the same deck.',
      'With Claude Cowork the deck leaves the app you are working in: a copy goes out to a separate product,',
      'a different file comes back, and you finish the job holding two files and reconciling them by hand.',
    ].join(' '),
  }),
  copilot: Object.freeze({
    id: 'copilot',
    diagram: 'modelLock',
    left: Object.freeze({
      eyebrow: 'Microsoft 365 Copilot',
      title: 'Model locked',
      outcome: 'One model, chosen for you',
      caption:
        'Copilot edits your open file too. What you do not get to choose is the model behind it.',
    }),
    right: Object.freeze({
      eyebrow: 'Restack',
      title: 'Model unlocked',
      outcome: 'Any model, your call',
      caption: RIGHT_CAPTION_COPILOT,
    }),
    alt: [
      'Two ways to work on the same deck.',
      'Microsoft 365 Copilot edits your open file, so nothing leaves your app and there is no round trip.',
      'What it does not offer is a choice of model: every pass runs on the one model it ships with.',
    ].join(' '),
  }),
});

/**
 * The variant for a `competitor` prop, falling back to the default.
 *
 * A marketing page must not white-screen because a prop was mistyped, dropped
 * in a refactor, or arrived as undefined from a data file, so an unknown value
 * resolves to the default variant instead of throwing. hasOwnProperty rather
 * than a bare lookup: 'constructor' and '__proto__' are strings a caller can
 * pass, and both would otherwise return something truthy that is not a variant.
 */
export function resolveCompetitor(id) {
  if (typeof id === 'string' && Object.prototype.hasOwnProperty.call(COMPETITORS, id)) {
    return COMPETITORS[id];
  }
  return COMPETITORS[DEFAULT_COMPETITOR];
}

// "A, B and C", and sane for shorter lists.
function listNames(names) {
  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * The text alternative. It carries the same comparison as the picture, model
 * names included, so a screen reader user is not told less than a sighted one,
 * and it differs per variant because the two arguments differ. The SVGs
 * themselves are decorative and hidden.
 */
export function buildTextAlternative(competitorId, modelLabels) {
  const variant = resolveCompetitor(competitorId);
  return [
    variant.alt,
    'With Restack the deck stays open and the agent edits that same file in place,',
    'and you choose which model runs each pass from a picker offering',
    `${listNames(modelLabels)},`,
    'so the same slide can be run again on a different model.',
  ].join(' ');
}

// ---------------------------------------------------------------------------
// Keyframes. The site's own motion vocabulary (see HomePage.styles.js):
// translate-and-fade (fadeInUp, slideInFromLeft), pulse, grow, shimmer. Every
// keyframe touches transform or opacity only, so nothing here can trigger
// layout.
// ---------------------------------------------------------------------------

// The uploaded copy leaving the app for the separate surface. Resting state is
// parked mid-corridor, which is the legible still frame.
const travelOut = keyframes`
  0% { transform: translate(-26px, 16px); opacity: 0; }
  8% { transform: translate(-22px, 13px); opacity: 1; }
  38% { transform: translate(24px, -15px); opacity: 1; }
  46%, 100% { transform: translate(24px, -15px); opacity: 0; }
`;

// The new, different file coming back down to the app.
const travelBack = keyframes`
  0%, 48% { transform: translate(30px, -8px); opacity: 0; }
  56% { transform: translate(25px, -6px); opacity: 1; }
  84% { transform: translate(-28px, 7px); opacity: 1; }
  92%, 100% { transform: translate(-28px, 7px); opacity: 0; }
`;

// The separate surface working on it.
const thinkPulse = keyframes`
  0%, 100% { opacity: 0.3; }
  50% { opacity: 1; }
`;

// The second file settling in the app. One shot with fill both: it lands once
// and then stays, because the two-file state is the point and a file that
// blinks out every loop would unsay the caption under the picture. It is never
// fully transparent for the same reason, it only firms up.
const landOnce = keyframes`
  0%, 55% { transform: translate(7px, -6px); opacity: 0.4; }
  72%, 100% { transform: translate(0, 0); opacity: 1; }
`;

// The edited chart column, rebuilt in the open file on every pass.
const barGrow = keyframes`
  0%, 14% { transform: scaleY(0.3); }
  52%, 100% { transform: scaleY(1); }
`;

// The shape being edited, marked while the edit lands.
const ringPulse = keyframes`
  0%, 100% { opacity: 0.35; }
  50% { opacity: 1; }
`;

// The site's shimmer, as a soft band that crosses the slide once per pass. It
// is clipped to the slide and sits off the left edge at rest, so the still
// frame simply has no sweep in it.
const sweep = keyframes`
  0% { transform: translateX(0); opacity: 0; }
  12% { opacity: 1; }
  62% { transform: translateX(232px); opacity: 1; }
  70%, 100% { transform: translateX(232px); opacity: 0; }
`;

// The site's pulse, on the affordance that fires each pass.
const softPulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.62; }
`;

// One keyframe, three delays: each model row holds the selection for a third of
// the cycle, so the picker visibly cycles through real names.
const pickCycle = keyframes`
  0% { opacity: 0; }
  4%, 30% { opacity: 1; }
  34%, 100% { opacity: 0; }
`;

// One pass is 4 seconds. The picker walks three models across three passes, so
// the two loops stay locked: pick a model, watch the slide rebuild, pick the
// next one.
const PASS = '4s';
const PICK_CYCLE = '12s';
const PICK_DELAYS = ['0s', '4s', '8s'];
const TRIP_CYCLE = '7s';

// Visually hidden, still read aloud. Same declaration DemoPage and PricingPage
// already use for their screen-reader-only text.
const SR_ONLY = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

/**
 * True when the visitor has asked for reduced motion.
 *
 * The CSS media query on each SVG is the primary guard and covers the
 * prerendered HTML before React hydrates. This hook is the second one: it lets
 * the component drop the animation declarations outright, which is both what a
 * test can assert on and what keeps the resting state authoritative in browsers
 * that report the preference through matchMedia only.
 */
function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(query.matches);
    const onChange = (event) => setReduced(event.matches);
    if (typeof query.addEventListener === 'function') {
      query.addEventListener('change', onChange);
      return () => query.removeEventListener('change', onChange);
    }
    if (typeof query.addListener === 'function') {
      query.addListener(onChange);
      return () => query.removeListener(onChange);
    }
    return undefined;
  }, []);

  return reduced;
}

/**
 * The animation shorthand for an element, or 'none' when motion is reduced.
 * Exported so the rule itself is testable without reading computed styles.
 */
export function motionValue(animation, reducedMotion) {
  return reducedMotion ? 'none' : animation;
}

// The public API is exactly one prop. No sx, no slots, no overrides: the two
// /vs pages own their section spacing and this component owns its own framing,
// which is what keeps the page and the graphic from having to agree on
// anything else.
const WorkflowComparison = ({ competitor }) => {
  const theme = useTheme();
  const reducedMotion = usePrefersReducedMotion();
  const variant = resolveCompetitor(competitor);

  // useId can contain colons, which are legal in an id but awkward in a url()
  // reference, so they are stripped. Per instance, so two of these on one page
  // cannot share a clip path.
  const instanceId = useId().replace(/[^a-zA-Z0-9-]/g, '');

  const accent = theme.palette.accentBubble?.warm || ACCENT_FALLBACK;
  const ink = theme.palette.text.primary;
  const inkA = (value) => alpha(ink, value);
  const accentA = (value) => alpha(accent, value);
  const surface = theme.palette.background.paper;
  const hairline = inkA(0.13);
  const muted = inkA(0.55);
  const anim = (value) => motionValue(value, reducedMotion);

  // Applied to every diagram. The nested media query is the no-JS guard: it
  // stops every descendant animation whatever the hook decided.
  const svgSx = {
    display: 'block',
    width: '100%',
    height: 'auto',
    fontFamily: BRAND_SANS_FONT,
    '@media (prefers-reduced-motion: reduce)': {
      '& *': { animation: 'none !important' },
    },
  };

  // -------------------------------------------------------------------------
  // Primitives. Plain functions rather than nested components: called inline
  // they cannot be remounted by React on every parent render.
  // -------------------------------------------------------------------------

  // A 16:9 slide, authored at SLIDE_W x SLIDE_H and placed with one uniform
  // scale. `kind` picks the content; `children` are drawn in the same 160 x 90
  // space, which is how the animated parts get in.
  const slideMock = ({ x, y, w, kind, tone = ink, dashed = false, children = null }) => {
    const scale = w / SLIDE_W;
    const toneA = (value) => alpha(tone, value);
    return (
      <g data-slide={String(w)} transform={`translate(${x},${y}) scale(${scale})`}>
        <rect
          data-slide-frame="true"
          x={0}
          y={0}
          width={SLIDE_W}
          height={SLIDE_H}
          rx={3}
          fill={surface}
          stroke={dashed ? inkA(0.3) : hairline}
          strokeWidth={1}
          strokeDasharray={dashed ? '4 3' : undefined}
          vectorEffect="non-scaling-stroke"
        />
        {kind === 'thumb' && (
          <>
            <rect x={14} y={16} width={78} height={9} rx={4} fill={toneA(0.3)} />
            <rect x={14} y={34} width={128} height={7} rx={3} fill={inkA(0.13)} />
            <rect x={14} y={48} width={104} height={7} rx={3} fill={inkA(0.13)} />
            <rect x={14} y={62} width={118} height={7} rx={3} fill={inkA(0.13)} />
          </>
        )}
        {(kind === 'source' || kind === 'edited') && (
          <>
            <rect
              x={12}
              y={11}
              width={74}
              height={7}
              rx={3.5}
              fill={kind === 'edited' ? tone : inkA(0.42)}
            />
            <rect x={12} y={23} width={48} height={4} rx={2} fill={inkA(0.18)} />
            <rect x={12} y={54} width={11} height={20} rx={1.5} fill={inkA(0.16)} />
            <rect x={27} y={44} width={11} height={30} rx={1.5} fill={inkA(0.16)} />
            <rect x={42} y={50} width={11} height={24} rx={1.5} fill={inkA(0.16)} />
            {kind === 'source' && (
              <rect x={57} y={38} width={11} height={36} rx={1.5} fill={inkA(0.16)} />
            )}
            <line x1={12} y1={74.6} x2={72} y2={74.6} stroke={inkA(0.14)} strokeWidth={0.8} />
            <rect x={86} y={34} width={62} height={4} rx={2} fill={inkA(0.13)} />
            <rect x={86} y={43} width={62} height={4} rx={2} fill={inkA(0.13)} />
            <rect x={86} y={52} width={50} height={4} rx={2} fill={inkA(0.13)} />
            {kind === 'source' ? (
              <rect x={86} y={61} width={56} height={4} rx={2} fill={inkA(0.13)} />
            ) : (
              <>
                <rect
                  x={86}
                  y={62}
                  width={38}
                  height={8}
                  rx={4}
                  fill={toneA(0.14)}
                  stroke={toneA(0.34)}
                  strokeWidth={0.8}
                />
                <rect x={90} y={65} width={22} height={2.4} rx={1.2} fill={toneA(0.55)} />
              </>
            )}
            <rect x={12} y={81} width={44} height={3} rx={1.5} fill={inkA(0.1)} />
          </>
        )}
        {kind === 'foreign' && (
          <>
            <rect x={12} y={11} width={104} height={7} rx={3.5} fill={inkA(0.34)} />
            <rect x={12} y={25} width={136} height={4.5} rx={2} fill={inkA(0.13)} />
            <rect x={12} y={34} width={136} height={4.5} rx={2} fill={inkA(0.13)} />
            <rect x={12} y={43} width={112} height={4.5} rx={2} fill={inkA(0.13)} />
            <rect x={12} y={56} width={70} height={6} rx={3} fill={inkA(0.16)} />
            <rect x={12} y={66} width={96} height={6} rx={3} fill={inkA(0.16)} />
            <rect x={12} y={76} width={54} height={6} rx={3} fill={inkA(0.16)} />
          </>
        )}
        {children}
      </g>
    );
  };

  // An application window: rounded frame, three dots, a title, an optional tag
  // chip on the right, and a hairline under the title bar.
  const windowFrame = ({ x, y, w, h, bar, title, tag, children }) => (
    <g>
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        rx={10}
        fill={surface}
        stroke={hairline}
        strokeWidth={1}
      />
      <circle cx={x + 13} cy={y + bar / 2} r={2.2} fill={inkA(0.2)} />
      <circle cx={x + 20.5} cy={y + bar / 2} r={2.2} fill={inkA(0.2)} />
      <circle cx={x + 28} cy={y + bar / 2} r={2.2} fill={inkA(0.2)} />
      <text x={x + 38} y={y + bar / 2 + 2.9} fontSize={8} fill={muted}>
        {title}
      </text>
      {tag ? (
        <>
          <rect
            x={x + w - 44}
            y={y + bar / 2 - 7}
            width={36}
            height={14}
            rx={4}
            fill={inkA(0.05)}
            stroke={hairline}
            strokeWidth={0.8}
          />
          <text
            x={x + w - 26}
            y={y + bar / 2 + 2.6}
            fontSize={6.6}
            textAnchor="middle"
            fill={muted}
            letterSpacing="0.06em"
          >
            {tag}
          </text>
        </>
      ) : null}
      <line x1={x} y1={y + bar} x2={x + w} y2={y + bar} stroke={hairline} strokeWidth={1} />
      {children}
    </g>
  );

  const arrowHead = (x, y, angle, fill) => (
    <path d="M0 0 L-7 -3.2 L-7 3.2 Z" fill={fill} transform={`translate(${x},${y}) rotate(${angle})`} />
  );

  const flowPath = (d, stroke) => (
    <path
      d={d}
      fill="none"
      stroke={stroke}
      strokeWidth={1.1}
      strokeDasharray="3 3"
      strokeLinecap="round"
    />
  );

  // -------------------------------------------------------------------------
  // THE LIVE WINDOW. One app window, the deck open in it, a filmstrip under it,
  // and a docked pane. Used by the Restack panel in both variants AND by the
  // Copilot panel, because Copilot edits the live file too: the two drawings
  // are the same window on purpose, and the pane is the whole difference.
  // -------------------------------------------------------------------------
  const liveWindow = ({ slot, tone, pane }) => {
    const clipId = `wc-slide-${instanceId}-${slot}`;
    const gradId = `wc-sweep-${instanceId}-${slot}`;
    const toneA = (value) => alpha(tone, value);
    return (
      <>
        <defs>
          <clipPath id={clipId}>
            <rect x={0} y={0} width={SLIDE_W} height={SLIDE_H} rx={3} />
          </clipPath>
          <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={tone} stopOpacity="0" />
            <stop offset="50%" stopColor={tone} stopOpacity="0.15" />
            <stop offset="100%" stopColor={tone} stopOpacity="0" />
          </linearGradient>
        </defs>

        {windowFrame({
          x: 4,
          y: 14,
          w: 392,
          h: 200,
          bar: 20,
          title: 'Deck.pptx',
          tag: 'PPTX',
          children: (
            <>
              {slideMock({
                x: 16,
                y: 44,
                w: 240,
                kind: 'edited',
                tone,
                children: (
                  <>
                    {/* the shape being edited */}
                    <Box
                      component="g"
                      sx={{ animation: anim(`${ringPulse} 2.6s ease-in-out infinite`) }}
                    >
                      <rect
                        x={7}
                        y={27}
                        width={70}
                        height={53}
                        rx={3}
                        fill="none"
                        stroke={toneA(0.5)}
                        strokeWidth={1}
                        strokeDasharray="4 3"
                        vectorEffect="non-scaling-stroke"
                      />
                    </Box>
                    {/* the column this pass rebuilds */}
                    <Box
                      component="g"
                      sx={{
                        transformBox: 'fill-box',
                        transformOrigin: 'bottom',
                        animation: anim(`${barGrow} ${PASS} ease-in-out infinite`),
                      }}
                    >
                      <rect x={57} y={32} width={11} height={42} rx={1.5} fill={tone} />
                    </Box>
                    {/* the edit landing, as the site's shimmer */}
                    <g clipPath={`url(#${clipId})`}>
                      <Box
                        component="g"
                        sx={{ animation: anim(`${sweep} ${PASS} ease-in-out infinite`) }}
                      >
                        <rect x={-56} y={0} width={56} height={SLIDE_H} fill={`url(#${gradId})`} />
                      </Box>
                    </g>
                  </>
                ),
              })}

              {/* the rest of the deck, so the frame reads as a deck and not a
                  single slide. Every thumbnail is the same 16:9 mock. */}
              {[16, 54, 92, 130].map((thumbX, index) => (
                <g key={thumbX}>
                  {slideMock({ x: thumbX, y: 188, w: 32, kind: 'thumb', tone })}
                  {index === 0 ? (
                    <rect
                      x={thumbX}
                      y={188}
                      width={32}
                      height={18}
                      rx={2}
                      fill="none"
                      stroke={toneA(0.55)}
                      strokeWidth={1.2}
                    />
                  ) : null}
                </g>
              ))}

              {/* the docked pane */}
              <rect
                x={272}
                y={44}
                width={116}
                height={156}
                rx={7}
                fill={inkA(0.03)}
                stroke={hairline}
                strokeWidth={1}
              />
              <line x1={272} y1={64} x2={388} y2={64} stroke={hairline} strokeWidth={1} />
              {pane}
            </>
          ),
        })}
      </>
    );
  };

  // A prompt bubble at the top of a pane. Same geometry both sides.
  const paneBubble = (tone) => (
    <>
      <rect
        x={284}
        y={72}
        width={98}
        height={17}
        rx={5}
        fill={alpha(tone, 0.1)}
        stroke={alpha(tone, 0.24)}
        strokeWidth={0.9}
      />
      <rect x={290} y={76.5} width={68} height={3} rx={1.5} fill={alpha(tone, 0.4)} />
      <rect x={290} y={82} width={50} height={3} rx={1.5} fill={alpha(tone, 0.4)} />
    </>
  );

  // -------------------------------------------------------------------------
  // THE RESTACK PANE. An open picker with real names, and a rerun affordance,
  // because running the same slide again on another model is the benefit.
  // -------------------------------------------------------------------------
  const restackPane = (
    <>
      <text x={280} y={59} fontSize={8.6} fontWeight={600} fill={ink}>
        Restack
      </text>
      <circle cx={378} cy={55.5} r={2.6} fill={accent} />
      {paneBubble(accent)}

      <rect
        x={278}
        y={96}
        width={104}
        height={14}
        rx={4}
        fill={surface}
        stroke={hairline}
        strokeWidth={0.9}
      />
      <text x={284} y={105.8} fontSize={7} fill={muted}>
        Model
      </text>
      <path
        d="M365.6 101.4 l3.2 3.4 l3.2 -3.4"
        fill="none"
        stroke={muted}
        strokeWidth={1.2}
        strokeLinecap="round"
      />

      {PICKER_MODEL_LABELS.map((label, index) => {
        const rowY = 118 + index * 14;
        return (
          <g key={label}>
            {/* The row name is never animated. Only the selection moves, so
                every model stays readable at every moment, and at rest the
                first row is the selected one. */}
            <Box
              component="g"
              sx={{
                opacity: index === 0 ? 1 : 0,
                animation: anim(`${pickCycle} ${PICK_CYCLE} linear ${PICK_DELAYS[index]} infinite`),
              }}
            >
              <rect
                x={278}
                y={rowY}
                width={104}
                height={13}
                rx={3}
                fill={accentA(0.14)}
                stroke={accentA(0.42)}
                strokeWidth={1}
              />
              <path
                d={`M365.5 ${rowY + 6.6} l2.3 2.5 l4.6 -5.4`}
                fill="none"
                stroke={accent}
                strokeWidth={1.3}
                strokeLinecap="round"
              />
            </Box>
            <text x={284} y={rowY + 9.2} fontSize={7.2} fill={ink}>
              {label}
            </text>
          </g>
        );
      })}

      <Box component="g" sx={{ animation: anim(`${softPulse} ${PASS} ease-in-out infinite`) }}>
        <rect
          x={278}
          y={164}
          width={104}
          height={16}
          rx={8}
          fill={accentA(0.1)}
          stroke={accentA(0.32)}
          strokeWidth={1}
        />
        <text x={330} y={174.8} fontSize={7.2} textAnchor="middle" fill={accent} fontWeight={600}>
          Run again
        </text>
      </Box>
      <text x={278} y={193} fontSize={6.8} fill={muted}>
        Same slide, new model
      </text>
    </>
  );

  // -------------------------------------------------------------------------
  // THE COPILOT PANE. The same pane, with the choice taken out: a locked model
  // field, one filled row whose value is deliberately UNNAMED (we do not claim
  // which model runs behind it), and two empty slots where the alternatives
  // would be.
  // -------------------------------------------------------------------------
  const lockedPane = (
    <>
      <text x={280} y={59} fontSize={8.6} fontWeight={600} fill={ink}>
        Copilot
      </text>
      <circle cx={378} cy={55.5} r={2.6} fill={inkA(0.22)} />
      {paneBubble(ink)}

      <rect
        x={278}
        y={96}
        width={104}
        height={14}
        rx={4}
        fill={inkA(0.05)}
        stroke={hairline}
        strokeWidth={0.9}
      />
      <text x={284} y={105.8} fontSize={7} fill={muted}>
        Model
      </text>
      <text x={376} y={105.8} fontSize={6.8} textAnchor="end" fill={muted}>
        Locked
      </text>

      {/* the one row on offer, with the padlock that says why it is the only
          one. Deliberately UNNAMED: which model runs behind Copilot is not a
          claim this page needs to make. Row positions match the Restack pane
          exactly, so the eye compares like with like. */}
      <rect
        x={278}
        y={118}
        width={104}
        height={13}
        rx={3}
        fill={inkA(0.07)}
        stroke={hairline}
        strokeWidth={1}
      />
      <rect x={284} y={122.4} width={50} height={4.2} rx={2.1} fill={inkA(0.32)} />
      <path
        d="M363.4 123.6 v-1.8 a2.4 2.4 0 0 1 4.8 0 v1.8"
        fill="none"
        stroke={inkA(0.4)}
        strokeWidth={1}
      />
      <rect x={362} y={123.6} width={7.4} height={5.4} rx={1.2} fill={inkA(0.4)} />

      {/* and the alternatives that are not there */}
      {[132, 146].map((slotY) => (
        <g key={slotY}>
          <rect
            x={278}
            y={slotY}
            width={104}
            height={13}
            rx={3}
            fill="none"
            stroke={inkA(0.15)}
            strokeWidth={1}
            strokeDasharray="4 3"
          />
          <rect x={284} y={slotY + 5.2} width={16} height={2.6} rx={1.3} fill={inkA(0.13)} />
        </g>
      ))}

      <rect
        x={278}
        y={164}
        width={104}
        height={16}
        rx={8}
        fill="none"
        stroke={inkA(0.16)}
        strokeWidth={1}
        strokeDasharray="4 3"
      />
      <text x={330} y={174.8} fontSize={7.2} textAnchor="middle" fill={muted}>
        No model choice
      </text>
      <text x={278} y={193} fontSize={6.8} fill={muted}>
        Same model, every pass
      </text>
    </>
  );

  // -------------------------------------------------------------------------
  // THE ROUND TRIP. Your app on the left, the separate surface on the right, a
  // copy going out along the top corridor and a DIFFERENT file coming back
  // along the bottom one, landing as a second deck in your app.
  // -------------------------------------------------------------------------
  const roundTripDiagram = () => (
    <>
      {windowFrame({
        x: 4,
        y: 40,
        w: 154,
        h: 174,
        bar: 18,
        title: 'Deck.pptx',
        children: (
          <>
            {slideMock({ x: 12, y: 66, w: 132, kind: 'source' })}
            {/* What the user is left holding. The original, and beside it the
                file that came back, which is not the same file. */}
            {slideMock({ x: 12, y: 154, w: 48, kind: 'source' })}
            <text x={36} y={192} fontSize={7} textAnchor="middle" fill={muted}>
              v1
            </text>
            <Box
              component="g"
              sx={{
                animation: anim(`${landOnce} ${TRIP_CYCLE} ease-out both`),
              }}
            >
              {slideMock({ x: 68, y: 154, w: 48, kind: 'foreign', dashed: true })}
              <text x={92} y={192} fontSize={7} textAnchor="middle" fill={muted}>
                v2
              </text>
            </Box>
          </>
        ),
      })}

      {windowFrame({
        x: 214,
        y: 8,
        w: 182,
        h: 150,
        bar: 18,
        title: 'Claude Cowork',
        children: (
          <>
            <rect
              x={224}
              y={34}
              width={104}
              height={16}
              rx={5}
              fill={inkA(0.05)}
              stroke={hairline}
              strokeWidth={0.9}
            />
            <rect x={230} y={38} width={7} height={9} rx={1.2} fill={inkA(0.3)} />
            <text x={242} y={45} fontSize={7} fill={ink}>
              deck.pptx
            </text>
            <Box
              component="g"
              sx={{ animation: anim(`${thinkPulse} 1.6s ease-in-out infinite`) }}
            >
              <circle cx={228} cy={62} r={2.2} fill={inkA(0.35)} />
              <circle cx={235} cy={62} r={2.2} fill={inkA(0.35)} />
              <circle cx={242} cy={62} r={2.2} fill={inkA(0.35)} />
            </Box>
            {slideMock({ x: 224, y: 74, w: 112, kind: 'foreign', dashed: true })}
            <text x={224} y={149} fontSize={7.2} fill={ink}>
              deck (2).pptx
            </text>
            <text x={344} y={149} fontSize={6.8} fill={muted}>
              a new file
            </text>
          </>
        ),
      })}

      {/* out: a copy leaves your app */}
      {flowPath('M161 94 C 180 94, 186 66, 206 62', inkA(0.28))}
      {arrowHead(208, 62, -8, inkA(0.32))}
      <text x={183} y={56} fontSize={6.8} textAnchor="middle" fill={muted}>
        a copy out
      </text>
      <Box component="g" sx={{ animation: anim(`${travelOut} ${TRIP_CYCLE} ease-in-out infinite`) }}>
        {slideMock({ x: 167, y: 70, w: 32, kind: 'source' })}
      </Box>

      {/* back: a different file returns, and lands beside the original */}
      {flowPath('M228 164 C 210 180, 186 176, 164 168', inkA(0.28))}
      {arrowHead(162, 168, 197, inkA(0.32))}
      <text x={197} y={196} fontSize={6.8} textAnchor="middle" fill={muted}>
        a different file back
      </text>
      <Box component="g" sx={{ animation: anim(`${travelBack} ${TRIP_CYCLE} ease-in-out infinite`) }}>
        {slideMock({ x: 181, y: 165, w: 32, kind: 'foreign', dashed: true })}
      </Box>
    </>
  );

  // -------------------------------------------------------------------------
  // Panel chrome.
  // -------------------------------------------------------------------------
  const panelSx = (isRestack) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: 1.25,
    borderRadius: '18px',
    border: `1px solid ${isRestack ? accentA(0.22) : inkA(0.1)}`,
    background: isRestack ? accentA(0.03) : inkA(0.02),
    boxShadow: isRestack ? `0 12px 34px ${accentA(0.1)}` : `0 10px 28px ${inkA(0.05)}`,
    p: { xs: 1.75, md: 2.5 },
    minWidth: 0,
  });

  const eyebrowSx = (isRestack) => ({
    display: 'inline-flex',
    alignSelf: 'flex-start',
    alignItems: 'center',
    maxWidth: '100%',
    px: 1,
    py: 0.35,
    borderRadius: '999px',
    background: isRestack ? accentA(0.1) : inkA(0.05),
    color: isRestack ? accent : muted,
    fontFamily: BRAND_SANS_FONT,
    fontSize: '0.66rem',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    lineHeight: 1.4,
  });

  const titleSx = {
    fontFamily: BRAND_SANS_FONT,
    fontSize: { xs: '1.05rem', md: '1.15rem' },
    fontWeight: 600,
    letterSpacing: '-0.015em',
    color: 'text.primary',
  };

  const outcomeSx = (isRestack) => ({
    display: 'inline-flex',
    alignSelf: 'flex-start',
    alignItems: 'center',
    gap: 0.75,
    maxWidth: '100%',
    px: 1.1,
    py: 0.5,
    borderRadius: '999px',
    border: `1px solid ${isRestack ? accentA(0.3) : inkA(0.12)}`,
    background: isRestack ? accentA(0.08) : inkA(0.035),
  });

  const outcomeTextSx = (isRestack) => ({
    fontFamily: BRAND_SANS_FONT,
    fontSize: '0.8rem',
    fontWeight: 600,
    lineHeight: 1.3,
    color: isRestack ? accent : 'text.primary',
  });

  const captionSx = {
    fontFamily: BRAND_SANS_FONT,
    fontSize: '0.82rem',
    fontWeight: 400,
    lineHeight: 1.55,
    color: 'text.secondary',
  };

  const panel = ({ copy, isRestack, diagram }) => (
    <Box sx={panelSx(isRestack)}>
      <Typography component="div" sx={eyebrowSx(isRestack)}>
        {copy.eyebrow}
      </Typography>
      <Typography component="h3" sx={titleSx}>
        {copy.title}
      </Typography>

      <Box
        component="svg"
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        role="presentation"
        aria-hidden="true"
        focusable="false"
        sx={svgSx}
      >
        {diagram}
      </Box>

      <Box sx={outcomeSx(isRestack)}>
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            flexShrink: 0,
            background: isRestack ? accent : inkA(0.35),
          }}
        />
        <Typography component="span" sx={outcomeTextSx(isRestack)}>
          {copy.outcome}
        </Typography>
      </Box>

      <Typography sx={captionSx}>{copy.caption}</Typography>
    </Box>
  );

  return (
    <Box
      component="figure"
      data-competitor={variant.id}
      data-reduced-motion={reducedMotion ? 'true' : 'false'}
      sx={{ m: 0, maxWidth: 1180, mx: 'auto' }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) minmax(0, 1fr)' },
          gap: { xs: 2.5, md: 3 },
          alignItems: 'stretch',
          '& > *': { minWidth: 0 },
        }}
      >
        {panel({
          copy: variant.left,
          isRestack: false,
          diagram:
            variant.diagram === 'roundTrip'
              ? roundTripDiagram()
              : liveWindow({ slot: 'left', tone: inkA(0.6), pane: lockedPane }),
        })}
        {panel({
          copy: variant.right,
          isRestack: true,
          diagram: liveWindow({ slot: 'right', tone: accent, pane: restackPane }),
        })}
      </Box>

      <Box component="figcaption" sx={SR_ONLY}>
        {buildTextAlternative(variant.id, PICKER_MODEL_LABELS)}
      </Box>
    </Box>
  );
};

export default WorkflowComparison;
